import { ICommand } from './ICommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getRelationshipMapping } from '../../lib/diagram/registries';
import {
  attrsWithRelationSemantics,
  relationSemanticsForSubtype,
} from '../../lib/graph/relationshipSemantics';
import VersionedComponentRegistry from '../../lib/diagram/VersionedComponentRegistry';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { validateShapeLimitOrPromptUpgrade } from '../../utils/shapeLimitUtils';
import { getProfileForDiagramType } from '../data-profiles/profileRegistry';
import type { DiagramType } from '../../lib/diagram/registries';
import { resolveEdgeBinding, applyEntityFieldEdgeChange, createEdgeBinding, removeBindingFromEdge } from '../../domains/data-binding/utils/edgeBinding';
import { getDataBinding } from '../../domains/data-binding/services/BoundShapeEditInterceptor';
import { readYjsValue } from '../../lib/diagram/yjsCompat';
import type * as Y from 'yjs';
import type {
  CommandDeltaContribution,
  IScenarioRecordable,
} from '../data-profiles/scenarios/ScenarioRecordable';
import {
  createEdgeId,
  createNodeId,
} from '../data-profiles/scenarios/ScenarioDelta';

export interface CreateEdgeParams {
  id?: string;
  type: string;
  from?: string;
  to?: string;
  category?: string;
  fromAnchor?: 'top'|'right'|'bottom'|'left';
  toAnchor?: 'top'|'right'|'bottom'|'left';
  fromOffset?: number;
  toOffset?: number;
  attrs?: Record<string, unknown>;
  version?: number;
  ydoc?: Y.Doc; // DEPRECATED: Use MutationManager instead
  sourceId?: string;
  userId?: string;
}

export class CreateEdgeCommand implements ICommand, IScenarioRecordable {
  id: string;
  name: string;
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateEdgeParams;
  private createdId?: string;
  // Stable synthetic id used when the command is recorded into a
  // scenario delta. We compute it once so `toScenarioDelta` and
  // `getUndoDelta` agree on the edge id inside the recorded delta,
  // independent of the live-mutation createdId.
  private scenarioEdgeId = createEdgeId(
    `scenario-edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  );

  constructor(params: CreateEdgeParams) {
    this.id = `create-edge-${Date.now()}`;
    this.name = 'Create Edge';
    this.description = `Create ${params.type} edge`;
    this.timestamp = Date.now();
    this.params = params;
    // Note: We no longer store ydoc, we use MutationManager which always has the latest doc
  }

  /**
   * Scenario recording — surface the to-be-created edge as an
   * `EdgeAddition` so scenario editing stages the edge in the delta
   * instead of committing to the live Yjs doc. Consumers (e.g.
   * `compassScenarioProjector`, `networkScenarioProjector`) append
   * phantom edges stamped `attrs.scenarioStatus: 'added'`.
   */
  toScenarioDelta(): CommandDeltaContribution {
    const from = this.params.from ?? '';
    const to = this.params.to ?? '';
    return {
      edgeAdditions: [
        {
          id: this.scenarioEdgeId,
          type: this.params.type,
          from: createNodeId(from),
          to: createNodeId(to),
          props: { ...(this.params.attrs ?? {}) },
        },
      ],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      edgeRemovals: [{ edgeId: this.scenarioEdgeId }],
    };
  }

  /**
   * Expose edge parameters for read-only introspection (e.g., layouting).
   */
  getParams(): CreateEdgeParams {
    return { ...this.params };
  }

  execute(): void {
    const store = useDiagramStore.getState();
    const selectedIds = Array.from(store.selectedIds);
    
    // Need exactly 2 nodes selected for edge creation
    if (!this.params.from || !this.params.to) {
      if (selectedIds.length !== 2) {
        console.warn('Cannot create edge: need exactly 2 nodes selected');
        return;
      }
    }
    
    // Generate or use provided ID
    this.createdId = this.params.id ?? `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Check shape limits
    const isNew = !store.edges.some(e => e.id === this.createdId);
    if (isNew && !validateShapeLimitOrPromptUpgrade(store)) {
      return; 
    }
    
    // Use provided from/to or get from selection
    const from = this.params.from || selectedIds[0];
    const to = this.params.to || selectedIds[1];
    
    let edgeType = this.params.type;
    let attrs: any = { ...(this.params.attrs || {}) };
    if (this.params.type && this.params.type !== 'line') {
      if (this.params.category === 'genogram') {
        const mapping = getRelationshipMapping('genogram' as any, this.params.type);
        if (mapping) {
          edgeType = mapping.edgeType as any;
          attrs = { ...(mapping.defaultEdgeProps || {}), ...(this.params.attrs || {}) };
          attrs = attrsWithRelationSemantics(
            attrs,
            relationSemanticsForSubtype({
              diagramType: 'genogram',
              subtypeId: this.params.type,
              qualifierSource: attrs,
              originalLabel: this.params.type,
            }),
            this.params.type,
          );
        }
      }
      if (this.params.category === 'org') {
        const mapping = getRelationshipMapping('org' as any, this.params.type);
        if (mapping) {
          edgeType = mapping.edgeType as any;
          attrs = { ...(mapping.defaultEdgeProps || {}), ...(this.params.attrs || {}) };
          attrs = attrsWithRelationSemantics(
            attrs,
            relationSemanticsForSubtype({
              diagramType: 'org',
              subtypeId: this.params.type,
              qualifierSource: attrs,
              originalLabel: this.params.type,
            }),
            this.params.type,
          );
        }
      }
      if (this.params.category === 'flowchart' || this.params.category === 'bpmn') {
        const mapping = getRelationshipMapping('flowchart' as any, this.params.type);
        if (mapping) {
          edgeType = mapping.edgeType as any;
          attrs = { ...(mapping.defaultEdgeProps || {}), ...(this.params.attrs || {}) };
          attrs = attrsWithRelationSemantics(
            attrs,
            relationSemanticsForSubtype({
              diagramType: 'flowchart',
              subtypeId: this.params.type,
              qualifierSource: attrs,
              originalLabel: this.params.type,
            }),
            this.params.type,
          );
        }
      }
    }
    
    let fromAnchor = this.params.fromAnchor;
    let toAnchor = this.params.toAnchor;
    let fromOffset = this.params.fromOffset;
    let toOffset = this.params.toOffset;

    if (this.params.category === 'org' && edgeType === 'reporting') {
      fromAnchor = 'top';
      toAnchor = 'bottom';
      fromOffset = 0.5;
      toOffset = 0.5;
    }

    // Use MutationManager to write to Yjs (single source of truth)
    const mutationManager = getNodeMutationManager();

    // Compute edge-table binding BEFORE addEdge so it's included in attrs
    // (entity-field binding updates a different node, so it goes after addEdge)
    const doc = mutationManager.getDoc();
    const sourceId = this.params.sourceId;
    const userId = this.params.userId;
    const diagramType = this.params.category as DiagramType | undefined;
    if (doc && sourceId && userId && this.createdId && diagramType) {
      const profile = getProfileForDiagramType(diagramType);
      if (profile) {
        const bindingMapping = resolveEdgeBinding(edgeType, profile);
        if (bindingMapping?.pattern === 'edge-table') {
          const shapesMap = doc.getMap('shapes');
          const fromShape = readYjsValue<Record<string, unknown> | undefined>(shapesMap.get(from));
          const toShape = readYjsValue<Record<string, unknown> | undefined>(shapesMap.get(to));
          const fromBinding = fromShape ? getDataBinding(fromShape) : null;
          const toBinding = toShape ? getDataBinding(toShape) : null;
          if (fromBinding?.recordId && toBinding?.recordId) {
            attrs.dataBinding = createEdgeBinding(this.createdId, bindingMapping, fromBinding.recordId, toBinding.recordId, sourceId, userId);
          }
        }
      }
    }

    // Create new edge (binding already included in attrs for edge-table pattern)
    const newEdge = {
      id: this.createdId,
      type: edgeType as any,
      version: this.params.version ?? VersionedComponentRegistry.getDefaultVersion(edgeType, 'connector') ?? 1,
      from,
      to,
      attrs,
    };
    (newEdge as any).fromAnchor = fromAnchor;
    (newEdge as any).toAnchor = toAnchor;
    (newEdge as any).fromOffset = fromOffset;
    (newEdge as any).toOffset = toOffset;

    try {
      console.debug('Debug SDI: CreateEdgeCommand.execute addEdge', {
        id: this.createdId,
        type: newEdge?.type,
        from,
        to
      });
    } catch {}
    mutationManager.addEdge(newEdge);

    // Entity-field binding: update the owner node's bound record field
    // (this modifies a different node, not the edge, so it runs after addEdge)
    if (doc && sourceId && userId && diagramType) {
      const profile = getProfileForDiagramType(diagramType);
      if (profile) {
        const bindingMapping = resolveEdgeBinding(edgeType, profile);
        if (bindingMapping?.pattern === 'entity-field') {
          applyEntityFieldEdgeChange(doc, bindingMapping, from, to, sourceId, userId);
        }
      }
    }

    console.log('[CreateEdgeCommand] ✅ Added edge via MutationManager:', {
      id: this.createdId,
      type: newEdge.type
    });
  }

  undo(): void {
    if (!this.createdId) return;

    // Remove edge binding before deleting the edge
    const mutationManager = getNodeMutationManager();
    const doc = mutationManager.getDoc();
    const sourceId = this.params.sourceId;
    const userId = this.params.userId;
    const diagramType = this.params.category as DiagramType | undefined;
    if (doc && sourceId && userId && diagramType) {
      const profile = getProfileForDiagramType(diagramType);
      if (profile) {
        const edgeType = this.params.type;
        const bindingMapping = resolveEdgeBinding(edgeType, profile);
        if (bindingMapping?.pattern === 'entity-field') {
          const from = this.params.from || '';
          applyEntityFieldEdgeChange(doc, bindingMapping, from, null, sourceId, userId);
        } else if (bindingMapping?.pattern === 'edge-table') {
          removeBindingFromEdge(doc, this.createdId);
        }
      }
    }

    try { console.debug('Debug SDI: CreateEdgeCommand.undo deleteEdge', { id: this.createdId }); } catch {}
    mutationManager.deleteEdge(this.createdId);

    console.log('[CreateEdgeCommand] ✅ Removed edge via MutationManager:', this.createdId);
  }

  redo(): void {
    this.execute();
  }
}
